import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import Image from "../../../components/images/Image";
import Shimmer from "../../AdminManagement/components/ViewAdminDetailsShimmer";
import useApi from "../../../services/useApi";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import config from "../../../config/config";
import { useNavigate, useParams } from "react-router";
import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
import { adminContents } from "../../AdminManagement/components/AdminManagementContent";
import moment from "moment";
import ToolTip from "../../../components/ToolTip/ToolTip";
import CustomModal from "../../../components/CustomModal";
import TextArea from "../../../components/inputfields/TextArea";
import { useDispatch } from "react-redux";
import { showToast } from "../../../components/toastMessage/toastSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";

type TransactionDetails = {
  id: string;
  transaction_id: string;
  transaction_date: string;
  requested_date: string;
  transaction_category: string;
  transaction_category_status: string;
  wallet_type: string;
  requested_by: string;
  requested_from: string;
  payment_type: string;
  amount: string;
  e_topup_stock_transferred: string | null;
  receiver_payment_source: string;
  sender_payment_source: string;
  sender_source_name: string;
  transaction_receipt: string;
  transaction_status: string;
  note: string;
  sender_location: string;
  receiver_location: string;
  phone_number: string | null;
  telecom_name: string | null;
  flagged_reason: string | null;
  rejection_reason: string | null;
  is_flagged: boolean;
  recipient: string;
  sender: string;
  requested_amount: string;
  recipient_location: string;
};

export default function ViewSuperAdminTransaction() {
  const { getAPI, PatchAPI } = useApi();
  const { translate, notEnglish } = useTranslation(adminContents);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const [transactionDetails, setTransactionDetails] =
    useState<TransactionDetails | null>(null);
  const pathname = useSelector((state: RootState) => state.selectedBankId.listOfUsers);
  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await getAPI(`transactions/super-admin/${id}`);
      if (!response?.error) {
        setTransactionDetails(response?.data?.data);
      } else {
        navigate("/not-found", {
          state: { route: "/admin-management/list-of-admins" },
        });
      }
    } catch (error) {
      console.error("Error fetching transaction details:", error);
    }
    setLoading(false);
  };

  useLayoutEffect(() => {
    fetchDetails();
  }, []);

  return (
    <div dir={notEnglish ? "rtl" : "ltr"}>
      <Breadcrumbs
        navPath={[
          "Zoodpay Money",
          "List of Super Admin Transactions",
          "View Transaction Details",
        ]}
        links={[
          pathname ? pathname : "/zoodpay-money/super-admin-transactions",
          pathname ? pathname : "/zoodpay-money/super-admin-transactions",
        ]}
        heading={`View Transaction`}
      />
      {!loading && transactionDetails ? (
        <div className="mt-1.5 bg-white h-maintableheight rounded-lg flex flex-col">
          <div className="px-6 py-5 border-b border-[#E5E9EB] flex justify-between items-center">
            <div className="flex items-center">
              <span className=" text-title3">Transaction ID - </span>
              <span
                title={"Transaction"}
                data-testid="transaction-id"
                className="text-heading3"
              >
                {transactionDetails.transaction_id || "-"}
              </span>
            </div>
          </div>
          {/* Dynamic field rendering starts here */}
          {(() => {
            // Main fields config (for non-Recharge)
            const mainFields = [
              {
                label: "Transaction Date & Time",
                value: transactionDetails?.transaction_date
                  ? moment(transactionDetails.transaction_date).format(
                    "DD-MM-YYYY, hh:mm A"
                  )
                  : "-",
              },
              {
                label: "Transaction Type (Status)",
                value: transactionDetails?.transaction_category
                  ? `${transactionDetails.transaction_category} (${transactionDetails.transaction_category_status})`
                  : "-",
              },
              {
                label: "Recipient",
                value: transactionDetails.recipient || "-",
              },
              {
                label: "Recipient Location",
                value: transactionDetails.recipient_location || "-",
              },
              {
                label: "Sender",
                value: transactionDetails.sender || "-",
              },
              {
                label: "Requested Amount",
                value: transactionDetails.requested_amount
                  ? `AFA ${transactionDetails.requested_amount}`
                  : "-",
              },
              {
                label: "Sent Amount",
                value: transactionDetails.amount
                  ? `AFA ${transactionDetails.amount}`
                  : "-",
              },
              {
                label: "Transaction Status",
                value: transactionDetails.transaction_status || "-",
              },
            ];
            // Recharge fields config
            const rechargeFields = [
              {
                label: "Transaction Date & Time",
                value: transactionDetails?.transaction_date
                  ? moment(transactionDetails.transaction_date).format(
                    "DD-MM-YYYY, hh:mm A"
                  )
                  : "-",
              },
              {
                label: "Phone Number",
                value: transactionDetails.phone_number || "-",
              },
              {
                label: "Amount",
                value: transactionDetails.amount
                  ? `AFA ${transactionDetails.amount}`
                  : "-",
              },
              {
                label: "Telecom",
                value: transactionDetails.telecom_name || "-",
              },
              {
                label: "Requested By",
                value: transactionDetails.recipient || "-",
              },
              {
                label: "Transaction Status",
                value: transactionDetails.transaction_status || "-",
              },
              {
                label: "Sender Location",
                value: transactionDetails.recipient_location || "-",
              },
            ];
            // Choose which fields to render
            const fieldsToRender =
              transactionDetails?.transaction_category !== "Recharge"
                ? mainFields
                : rechargeFields;
            return (
              <div
                className={`flex-grow px-6 pb-4 my-4 h-viewAdminHeight ${getFirefoxCssFix(
                  "scroll"
                )} overflow-y-scroll`}
              >
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-6">
                  {fieldsToRender.map((field: any, idx) => (
                    <div
                      key={idx}
                      className={
                        field.label === "Transaction Receipt"
                          ? "group relative col-span-2 md:col-span-1"
                          : undefined
                      }
                    >
                      <h1 className="text-body2 text-primary_text">
                        {field.label}
                      </h1>
                      <p className="text-body1">
                        {field?.render ? field?.render() : field.value}
                      </p>
                    </div>
                  ))}
                </div>
                {/* Note and reasons */}
                {transactionDetails?.transaction_category !== "Recharge" && <div className="mt-6">
                  <h1 className="text-body2 w-full text-primary_text">
                    Transaction Receipt
                  </h1>
                  <div>
                    {transactionDetails?.transaction_receipt ? (
                      <div className="w-1/3 flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1">
                        <p className="more-lines">
                          {transactionDetails?.transaction_receipt
                            ?.split("/")
                            .pop()}
                        </p>
                        <ToolTip
                          ToolTipItem={
                            transactionDetails?.transaction_receipt
                              ?.split("/")
                              .pop() || ""
                          }
                          top="top-5 left-5"
                        />
                        <Image
                          src="file-eye"
                          className="cursor-pointer "
                          objectFit={"contain"}
                          onClick={() => {
                            const fileUrl =
                              config?.cdnURL +
                              "public/" +
                              transactionDetails.transaction_receipt;
                            const link = document.createElement("a");
                            link.href = fileUrl;
                            link.target = "_blank";
                            link.rel = "noopener noreferrer";
                            link.click();
                          }}
                        />
                      </div>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>}
                <div className="mt-6">
                  <h1 className="text-body2 w-full text-primary_text">Note</h1>
                  <p className="text-body1 break-all">
                    {transactionDetails.note || "-"}
                  </p>
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        <Shimmer />
      )}
    </div>
  );
}
